import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { Example } from '@/Pages/Example';
import { Run } from '@/Pages/Run';
import { RunAnnotationsCrudPane } from '@/components/RunAnnotationCrudPane/RunAnnotationsCrudPane';
import { SplitViewPane } from '@/components/SplitViewPane';
import { useStateFromSearchParams } from '@/hooks/useStateFromSearchParams';
import { useOrganizationId, useRun } from '@/hooks/useSwr';
import {
  ComparisonViewColumn,
  DatasetSchema,
  ExampleSchemaWithRunsAndOptionalFields,
  RunSchema,
  SessionSchema,
} from '@/types/schema';
import {
  appDatasetsPath,
  appExamplePath,
  appOrganizationPath,
  appPublicDatasetsPath,
  appPublicPath,
  appRunPath,
  appSessionPath,
} from '@/utils/constants';
import { cn } from '@/utils/tailwind';

import { EXPERIMENT_OUTPUT_WIDTH_NUMBER } from '../constants';
import { ExperimentLanguage } from '../types';
import { TExampleDiff } from '../types';
import { getRepetitionNumber } from '../utils/getRepetitionNumber';
import { TGetRunDiffArgs } from '../utils/useGetExperimentDiffs';
import { useSortedRepetitionRuns } from '../utils/useSortedRepetitionRuns';
import { AggregateFeedbackChips } from './AggregateFeedbackChips';
import DatasetSessionCompareRepetition from './DatasetSessionCompareRepetition';

export const DatasetSessionCompareRepetitionsDetailPane = (props: {
  detailExample: ExampleSchemaWithRunsAndOptionalFields;
  datasetShareToken?: string;
  sessions?: SessionSchema[];
  columns?: ComparisonViewColumn[];
  columnId?: string;
  dataset?: DatasetSchema;
  language?: ExperimentLanguage;
  exampleDetailPaneOpen: boolean;
  selectedFeedbackKey?: string | null;
  setExampleDetailPaneOpen: (open: boolean) => void;
  mutateInfiniteExamples: () => void;
  diffs?: TExampleDiff;
  getRunDiffs: (args: TGetRunDiffArgs) => void;
}) => {
  const runs: RunSchema[] = useMemo(
    () =>
      props.detailExample?.runs
        .filter((run) => run.session_id === props.columnId)
        .filter(Boolean) as RunSchema[],
    [props.detailExample?.runs, props.columnId]
  );
  const allFeedbackKeys = useMemo(
    () =>
      new Set(
        runs?.flatMap((run) => Object.keys(run.feedback_stats ?? {})) ?? []
      ),
    [runs]
  );

  const defaultFeedbackKey = useMemo(
    () =>
      props.selectedFeedbackKey &&
      allFeedbackKeys?.has(props.selectedFeedbackKey)
        ? props.selectedFeedbackKey
        : allFeedbackKeys?.size
        ? allFeedbackKeys[0]
        : null,
    [allFeedbackKeys, props.selectedFeedbackKey]
  );

  const [sortByFeedbackKey, setSortByFeedbackKey] =
    useState(defaultFeedbackKey);

  const [sortByDescending, setSortByDescending] = useState(false);

  // used to get repetition number by default sorting order
  const defaultSortedRuns = useSortedRepetitionRuns(
    runs,
    false,
    defaultFeedbackKey
  );

  const sortedRuns = useSortedRepetitionRuns(
    runs,
    sortByDescending,
    sortByFeedbackKey
  );

  const sortedRunIds = useMemo(
    () => sortedRuns?.map((run) => run.id) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedRuns, sortByDescending, sortByFeedbackKey]
  );

  const currentSession = props.sessions?.find(
    (session) => session.id === props.columnId
  );
  const currentColumn = props.columns?.find(
    (column) => column['columnId'] && column['columnId'] === props.columnId
  );

  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeRun, setActiveRun] = useStateFromSearchParams(
    'activeSession',
    sortedRuns?.[0]?.id ?? null
  );

  const stringifiedRunIds = sortedRunIds.join(',');

  useEffect(() => {
    if (!activeRun) {
      setActiveRun(sortedRuns?.[0]?.id ?? null);
    } else {
      scrollToItem(activeRun);
    }
  }, [stringifiedRunIds, activeRun, setActiveRun]);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [detailRunId, setDetailRunId] = useStateFromSearchParams(
    'detailRepetitionsPaneDetailRunId',
    null
  );
  const [annotationDetailRunId, setAnnotationDetailRunId] =
    useStateFromSearchParams('detailRepetitionsPaneAnnotationRunId', null);

  const [haveComponentsExpanded, setHaveComponentsExpanded] = useState(false);

  const handleResize = useDebouncedCallback(
    () => {
      if (itemRefs.current[0]) {
        const rect = itemRefs.current[0].getBoundingClientRect();
        setHaveComponentsExpanded(
          rect.width > EXPERIMENT_OUTPUT_WIDTH_NUMBER + 5
        );
      }
    },
    100,
    { leading: true }
  );

  useEffect(() => {
    const currentItem = itemRefs.current[0];

    const resizeObserver = new ResizeObserver(handleResize);
    if (itemRefs.current[0]) {
      resizeObserver.observe(itemRefs.current[0]);
    }

    return () => {
      if (currentItem) {
        resizeObserver.unobserve(currentItem);
      }
    };
  }, [handleResize, props.detailExample]);

  const annotationDetailRun = annotationDetailRunId
    ? props.detailExample?.runs?.find((r) => r.id === annotationDetailRunId)
    : null;

  let detailRun = detailRunId
    ? props.detailExample?.runs?.find((r) => r.id === detailRunId)
    : null;

  const shouldFetchDetailRun = detailRunId && !detailRun;
  const detailRunSwr = useRun({
    id: shouldFetchDetailRun ? detailRunId : null,
    shareToken: {
      datasetShareToken: props.datasetShareToken,
    },
    withContents: true,
    sessionId: detailRun?.session_id,
    startTime: detailRun?.start_time,
  });

  if (shouldFetchDetailRun) {
    detailRun = detailRunSwr.data;
  }
  const navigate = useNavigate();
  const organizationId = useOrganizationId();

  const scrollToItem = useCallback(
    (section: string) => {
      const index = sortedRunIds.indexOf(section);
      itemRefs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    },
    [sortedRunIds]
  );

  const handleScroll = useCallback(() => {
    let maxVisibility = 0;
    let currentActiveRun = activeRun;

    itemRefs.current.forEach((section, index) => {
      if (section) {
        const rect = section.getBoundingClientRect();
        const containerRectLeft =
          scrollContainerRef.current?.getBoundingClientRect().left ?? 0;
        const visibleWidth =
          Math.min(
            rect.right - containerRectLeft,
            scrollContainerRef.current?.clientWidth || 0
          ) - Math.max(rect.left - containerRectLeft, 0);
        if (visibleWidth > maxVisibility + 10) {
          maxVisibility = visibleWidth;
          currentActiveRun = sortedRunIds[index];
        }
      }
    });

    if (currentActiveRun !== activeRun) {
      setActiveRun(currentActiveRun);
    }
  }, [activeRun, setActiveRun, sortedRunIds]);

  // Debounce scroll handler to avoid a re-render mid scroll
  const handleScrollDebounced = useDebouncedCallback(handleScroll, 100);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScrollDebounced);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScrollDebounced);
      }
    };
  }, [handleScrollDebounced, props.detailExample]);

  const onSort = useCallback((feedbackKey: string, descending: boolean) => {
    setSortByDescending(descending);
    setSortByFeedbackKey(feedbackKey);
  }, []);

  return props.detailExample ? (
    <div
      className={cn(
        'inset-0 flex h-[calc(100vh-60px)] flex-grow flex-col overflow-hidden'
      )}
    >
      <div className="flex flex-col gap-3 px-6 pb-6 pt-3">
        <div className="line-clamp-1 break-all text-xl font-semibold">
          {currentSession?.name
            ? `${currentSession.name}: `
            : currentColumn?.['columnName']
            ? `${currentColumn['columnName']}: `
            : ''}
          {sortedRunIds.length} repetitions
          {props.detailExample.name ? ` of ${props.detailExample.name}` : ''}
        </div>
        <div className="flex gap-2">
          <AggregateFeedbackChips
            runs={sortedRuns}
            allowTruncation
            onSort={onSort}
            sortingByFeedbackKey={sortByFeedbackKey ?? undefined}
            sortingByDescending={sortByDescending}
            onMutate={props.mutateInfiniteExamples}
            disablePopover={!!props.datasetShareToken}
          />
        </div>
      </div>

      <h2 className="flex w-full items-center bg-secondary text-sm font-medium">
        <span className="px-4 py-2 text-sm font-medium">Output</span>
        {sortedRunIds.length > 2 && !haveComponentsExpanded && (
          <div className="flex border-secondary px-1.5 pt-3">
            {sortedRunIds.map((runId) => {
              const selected = activeRun === runId;
              return (
                <button
                  type="button"
                  key={runId}
                  onClick={() => {
                    scrollToItem(runId);
                  }}
                  className={cn(
                    '-mb-px max-w-[250px] border-b-2 border-b-transparent px-2 pb-[10px] font-semibold leading-none focus:outline-none',
                    selected &&
                      'border-black text-black dark:border-white dark:text-white'
                  )}
                >
                  <span className="line-clamp-1 whitespace-normal break-words break-all">
                    Repetition {getRepetitionNumber(defaultSortedRuns, runId)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </h2>
      <div className="flex flex-1 flex-col overflow-hidden">
        <div
          className="flex gap-3 overflow-x-auto p-3 pb-9"
          style={{ flex: 1 }}
          ref={scrollContainerRef}
        >
          {sortedRuns.map((run, idx) => (
            <DatasetSessionCompareRepetition
              run={run}
              repetitionNumber={getRepetitionNumber(defaultSortedRuns, run.id)}
              example={props.detailExample}
              sortedRunIds={sortedRunIds}
              idx={idx}
              dataset={props.dataset}
              datasetShareToken={props.datasetShareToken}
              language={props.language}
              setDetailRunId={setDetailRunId}
              setAnnotationDetailRunId={setAnnotationDetailRunId}
              mutateInfiniteExamples={props.mutateInfiniteExamples}
              itemRefs={itemRefs}
              getRunDiffs={props.getRunDiffs}
              diffs={props.diffs}
            />
          ))}
        </div>
        <SplitViewPane
          open={!!detailRunId}
          sidePaneId="detailPaneTraceDetail"
          onClose={() => {
            setDetailRunId(null);
          }}
          onExpand={() => {
            if (detailRun) {
              const targetUrl = props.datasetShareToken
                ? `/${appPublicPath}/${props.datasetShareToken}/${appPublicDatasetsPath}/${detailRun.session_id}/${appSessionPath}/${appRunPath}/${detailRun?.id}?trace_id=${detailRun?.trace_id}&start_time=${detailRun?.start_time}`
                : detailRun?.app_path ?? '';

              navigate(targetUrl);
            }
          }}
          className="relative"
          title={''}
        >
          <div className="absolute inset-0 flex flex-grow flex-col overflow-auto px-4">
            <Run
              runId={detailRun?.id}
              traceId={detailRun?.trace_id}
              navigateToRun={(runId) => setDetailRunId(runId)}
              shareTokenProp={{ datasetShareToken: props.datasetShareToken }}
              runSessionId={detailRun?.session_id}
            />
          </div>
        </SplitViewPane>
        <RunAnnotationsCrudPane
          isOpen={!!annotationDetailRunId}
          run={annotationDetailRun}
          onClose={() => setAnnotationDetailRunId(null)}
          onFeedbackGiven={props.mutateInfiniteExamples}
        />
        <SplitViewPane
          open={props.exampleDetailPaneOpen}
          sidePaneId="datasetSessionCompareExampleInDetailPane"
          onClose={() => {
            props.setExampleDetailPaneOpen(false);
          }}
          onExpand={() => {
            const targetUrl = props.datasetShareToken
              ? `/${appPublicPath}/${props.datasetShareToken}/${appPublicDatasetsPath}/${props.detailExample?.id}/${appExamplePath}`
              : `/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${props.dataset?.id}/${appExamplePath}/${props.detailExample?.id}`;
            navigate(targetUrl);
          }}
          title={null}
          className="relative"
        >
          <div className="absolute inset-0 flex flex-grow flex-col gap-4 overflow-auto p-4">
            <Example
              exampleId={props.detailExample.id}
              datasetId={props.dataset?.id}
              onMutate={() => props.mutateInfiniteExamples()}
            />
          </div>
        </SplitViewPane>
      </div>
    </div>
  ) : null;
};
